<script lang="ts">
  import {onMount} from 'svelte';
  import {
    setInitialProps,
    fetchDeal,
    fetchSiteKey,
    apiError,
    accessLink,
    resetForms,
    recaptchaToken,
    EmbeddedFlowState,
    viewState,
    deal,
  } from 'Store/InvestorFlowStore';
  import {isEmbedded} from 'Store/embedded/embeddedFlowStore';
  import {transitionToApplication} from 'Utility/investorFlow/transitionFunction';
  import {whiteLabeling} from 'Store/WhiteLabelStore';
  import MultiStepProductPage from 'Components/InvestorFlow/v2/ProductPage.svelte';
  import PostingInvestor from 'Components/InvestorFlow/PostingInvestor.svelte';
  import QuestionFlow from 'Components/InvestorFlow/QuestionFlow.svelte';
  import LoadingSpinner from 'Components/ui/LoadingSpinner.svelte';
  import RecaptchaPage from 'Components/InvestorFlow/RecaptchaPage.svelte';
  import EmbedFlow from 'Components/InvestorFlow/EmbedFlow.svelte';
  import type {CssColor} from 'Catalyst/utility/theme';
  import {ThemeContext} from 'Catalyst/components/ThemeContext';

  export let apiUrl: URL;
  export let dealId: string;
  export let invitationUid: string;
  export let displayLogo: boolean;
  export let hosted: boolean;
  export let inTemplatePage: boolean;

  const searchParams = new URLSearchParams(window.location.search);
  const isReturningInvestor: boolean = searchParams.has('returning_investor');

  let currentView;
  let bgImage = '';
  let flowContainer: HTMLElement;

  onMount(async () => {
    isEmbedded.set(true);

    setInitialProps({
      apiUrl,
      dealId,
      hosted,
      displayLogo,
      invitationUid,
      dealMakerUrl: null,
    });
    await fetchDeal();
    await fetchSiteKey();

    if ($accessLink) {
      transitionToApplication();
    } else if (isReturningInvestor && !$recaptchaToken) {
      viewState.set(EmbeddedFlowState.RECAPTCHA);
    } else {
      viewState.set(EmbeddedFlowState.PRODUCT_PAGE);
    }
  });

  window.onmessage = function (e: MessageEvent) {
    if (e.data === 'expired-session') {
      resetForms();
    }
  };

  $: {
    switch ($viewState) {
      case EmbeddedFlowState.LOADING_SPINNER:
        currentView = LoadingSpinner;
        break;
      case EmbeddedFlowState.RECAPTCHA:
        currentView = RecaptchaPage;
        break;
      case EmbeddedFlowState.PRODUCT_PAGE:
        currentView = MultiStepProductPage;
        break;
      case EmbeddedFlowState.ACCESS_LINK:
        if (!hosted) {
          window.location.assign(window.location.href);
          break;
        }

        window.location.assign($accessLink);
        break;
      case EmbeddedFlowState.EMBED_COMPONENT:
        currentView = EmbedFlow;
        break;
      case EmbeddedFlowState.POSTING_INVESTOR:
        currentView = PostingInvestor;
        break;
      case EmbeddedFlowState.QUESTION_FLOW:
        currentView = QuestionFlow;
        break;
    }

    if (!inTemplatePage && $whiteLabeling && $whiteLabeling.background_image) {
      bgImage = `url(${$whiteLabeling.background_image})`;
    }
  }
  $: shouldGenerateTheme =
    $deal?.feature_flags?.generative_themed_checkout &&
    $whiteLabeling &&
    $whiteLabeling.primary_color;
  $: brandColors = shouldGenerateTheme
    ? [
        {
          key: 'brand-color',
          value: $whiteLabeling.primary_color as CssColor,
        },
      ]
    : [];
</script>

<ThemeContext {brandColors}>
  <div
    class="investor-flow-container"
    class:background-color={!!$whiteLabeling.background_image}
    class:template-embedded={inTemplatePage}
    style="background-image: {bgImage};"
    data-testid="investor-flow-container"
    bind:this={flowContainer}
  >
    {#if $apiError}
      <div class="error">{$apiError}</div>
    {/if}
    <svelte:component this={currentView} />
  </div>
</ThemeContext>

<style lang="postcss">
  .error {
    background-color: #ff5b5b;
    color: white;
    border-radius: 0.1rem;
    text-align: center;
  }

  .background-color {
    background-color: var(
      --dm-bg-primary-color,
      var(--palettes-neutral-0, #ffffff)
    );
  }

  .investor-flow-container {
    display: flex;
    flex-direction: column;
    height: 100%;
    background-color: #ffffff00;
    background-size: auto 50%;
    background-repeat: no-repeat;
    background-position: 0px 0px;
    background-size: 100% 100%;

    & :global(.title-default-color) {
      color: black;
    }
    & :global(.title-wl-color) {
      color: white;
    }

    & :global(.section-heading) {
      text-transform: capitalize;
      font-size: 0.88rem;
      line-height: 1.38rem;
      font-weight: 500;
    }
    & :global(.section-subtitle) {
      text-transform: none;
      font-size: 0.75rem;
      color: #8c8c8c;
      line-height: 1.25rem;
      size: 0.75rem;
    }

    &.template-embedded {
      /* custom landing page fixes */
      & :global(input),
      & :global(button) {
        font-family: inherit;
        font-size: inherit;
      }

      & :global(h3) {
        font-size: 1.75rem;
      }
      & :global(h1),
      & :global(h2),
      & :global(h3),
      & :global(h4),
      & :global(h5),
      & :global(h6) {
        font-weight: 500;
        margin-top: 0;
        margin-bottom: 0.5rem;
        line-height: 1.2;
      }
      & :global(::placeholder) {
        color: #b6b2af !important;
      }
    }
  }

  @media only screen and (min-width: 62.5rem) {
    .investor-flow-container {
      background-size: 100% 100%;
    }
  }
</style>
